import React, { useState, useCallback } from 'react';
import { Eraser, UploadCloud, RefreshCw, Download, Paintbrush, Layers, Check, Image as ImageIcon, Sparkles, ShieldCheck } from 'lucide-react';
import { BackgroundBrushModal } from '../edit/BackgroundBrushModal';
import { CreatorBacklink } from '../ui/CreatorBacklink';

export function StandaloneBgRemover() {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [processedUrl, setProcessedUrl] = useState<string | null>(null);

  const [selectedColor, setSelectedColor] = useState<string>('#ffffff');
  const [tolerance, setTolerance] = useState<number>(30);
  const [feather, setFeather] = useState<number>(2);
  const [bgType, setBgType] = useState<'transparent' | 'color' | 'gradient'>('transparent');
  const [replacementColor, setReplacementColor] = useState<string>('#4f46e5');

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [showBrushModal, setShowBrushModal] = useState<boolean>(false);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (imageUrl) URL.revokeObjectURL(imageUrl);

    const url = URL.createObjectURL(file);
    setImageFile(file);
    setImageUrl(url);
    setProcessedUrl(null);

    // Auto run removal on upload
    processRemoval(file, selectedColor, tolerance, feather, bgType, replacementColor);
  };

  const processRemoval = (
    file: File,
    colorHex: string,
    tol: number,
    feath: number,
    bgMode: string,
    replColor: string
  ) => {
    setIsProcessing(true);
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        setIsProcessing(false);
        URL.revokeObjectURL(url);
        return;
      }

      ctx.drawImage(img, 0, 0);
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imgData.data;

      const hex = colorHex.replace('#', '');
      const targetR = parseInt(hex.substring(0, 2), 16) || 255;
      const targetG = parseInt(hex.substring(2, 4), 16) || 255;
      const targetB = parseInt(hex.substring(4, 6), 16) || 255;

      const maxDiff = (tol / 100) * 441.67;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        const diff = Math.sqrt(
          Math.pow(r - targetR, 2) + Math.pow(g - targetG, 2) + Math.pow(b - targetB, 2)
        );

        if (diff <= maxDiff) {
          if (feath > 0 && diff > maxDiff - feath * 10) {
            const alphaFactor = (diff - (maxDiff - feath * 10)) / (feath * 10);
            data[i + 3] = Math.round(alphaFactor * 255);
          } else {
            data[i + 3] = 0;
          }
        }
      }

      ctx.putImageData(imgData, 0, 0);

      if (bgMode !== 'transparent') {
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = img.width;
        finalCanvas.height = img.height;
        const finalCtx = finalCanvas.getContext('2d');

        if (finalCtx) {
          if (bgMode === 'color') {
            finalCtx.fillStyle = replColor;
            finalCtx.fillRect(0, 0, img.width, img.height);
          } else if (bgMode === 'gradient') {
            const grad = finalCtx.createLinearGradient(0, 0, img.width, img.height);
            grad.addColorStop(0, '#4f46e5');
            grad.addColorStop(0.5, '#7c3aed');
            grad.addColorStop(1, '#ec4899');
            finalCtx.fillStyle = grad;
            finalCtx.fillRect(0, 0, img.width, img.height);
          }

          finalCtx.drawImage(canvas, 0, 0);
          setProcessedUrl(finalCanvas.toDataURL('image/png'));
          setIsProcessing(false);
          URL.revokeObjectURL(url);
          return;
        }
      }

      setProcessedUrl(canvas.toDataURL('image/png'));
      setIsProcessing(false);
      URL.revokeObjectURL(url);
    };

    img.src = url;
  };

  const handleDownload = () => {
    if (!processedUrl || !imageFile) return;
    const link = document.createElement('a');
    link.href = processedUrl;
    link.download = `bg_removed_${imageFile.name.replace(/\.[^/.]+$/, '')}.png`;
    link.click();
  };

  const handleStartOver = () => {
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    setImageFile(null);
    setImageUrl(null);
    setProcessedUrl(null);
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {!imageFile ? (
        /* Standalone App Upload Hero Dropzone */
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/40 dark:border-slate-800 shadow-2xl text-center space-y-6 max-w-3xl mx-auto">
          <div className="p-5 bg-gradient-to-tr from-pink-600 via-purple-600 to-indigo-600 text-white rounded-3xl w-20 h-20 mx-auto flex items-center justify-center shadow-xl shadow-pink-500/30 animate-pulse-glow">
            <Eraser className="w-10 h-10" />
          </div>

          <div className="space-y-2">
            <h1 className="text-3xl font-black text-slate-900 dark:text-slate-100 tracking-tight">
              Blivix AI Background Remover
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-lg mx-auto">
              Remove image backgrounds 100% locally in your browser. Perfect for e-commerce products, logos, headshots & social media graphics!
            </p>
          </div>

          <label className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 hover:from-pink-700 hover:to-indigo-700 text-white font-bold text-base rounded-2xl shadow-xl shadow-pink-500/30 cursor-pointer transition-all hover:scale-105">
            <UploadCloud className="w-6 h-6" />
            <span>Upload Image to Remove BG</span>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/avif"
              className="hidden"
              onChange={handleImageUpload}
            />
          </label>

          <div className="flex flex-wrap justify-center gap-3 pt-2 text-xs text-slate-400 font-semibold">
            <span>PNG, JPG, WebP Supported</span>
            <span>•</span>
            <span>100% Private Client-Side Memory</span>
            <span>•</span>
            <span>Free Unlimited Exports</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-slate-200/60 dark:border-slate-800">
            <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-2xl text-left border border-slate-200/60 dark:border-slate-700/60">
              <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200">🏁 Transparent PNG Output</h4>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Isolated subjects with alpha channel</p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-2xl text-left border border-slate-200/60 dark:border-slate-700/60">
              <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200">🖌️ Magic Restore Brush</h4>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Paint over eyes or details to restore</p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-2xl text-left border border-slate-200/60 dark:border-slate-700/60">
              <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200">🎨 Backdrop Replacer</h4>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Swap with solid colors or gradients</p>
            </div>
          </div>
        </div>
      ) : (
        /* Active Background Removal Interface */
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-3xl p-6 border border-white/40 dark:border-slate-800 shadow-2xl space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pb-4 border-b border-slate-200/70 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-gradient-to-tr from-pink-600 to-purple-600 text-white rounded-xl shadow-md">
                <Eraser className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate max-w-xs sm:max-w-md">
                  {imageFile.name}
                </h3>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                  {(imageFile.size / 1024).toFixed(1)} KB • 100% Local Processing
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleStartOver}
              className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Upload New Image
            </button>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
            {/* Left Preview Viewport (xl:col-span-7 2xl:col-span-8) */}
            <div className="xl:col-span-7 2xl:col-span-8 space-y-4 xl:sticky xl:top-20">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl min-h-[350px] aspect-square flex items-center justify-center border border-slate-200 dark:border-slate-800 bg-[radial-gradient(#334155_1px,transparent_1px)] [background-size:16px_16px]">
                {processedUrl ? (
                  <img
                    src={processedUrl}
                    alt="Background Removed Output"
                    className="max-h-full max-w-full object-contain p-4 rounded-lg"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-slate-400">
                    <RefreshCw className="w-8 h-8 animate-spin text-pink-500" />
                    <span className="text-xs font-bold">Stripping background...</span>
                  </div>
                )}
              </div>

              {/* Download Action Bar */}
              {processedUrl && (
                <div className="flex flex-col sm:flex-row justify-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 hover:from-pink-700 hover:to-indigo-700 text-white font-bold text-sm rounded-2xl shadow-xl shadow-pink-500/30 flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                  >
                    <Download className="w-5 h-5" />
                    <span>Download Background Removed Image (PNG)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowBrushModal(true)}
                    className="w-full sm:w-auto px-5 py-3.5 bg-emerald-50 dark:bg-emerald-950/60 hover:bg-emerald-100 text-emerald-700 dark:text-emerald-300 font-bold text-xs rounded-2xl border border-emerald-300 dark:border-emerald-800 flex items-center justify-center gap-2 transition-all"
                  >
                    <Paintbrush className="w-4 h-4" />
                    <span>🖌️ Magic Restore Brush</span>
                  </button>
                </div>
              )}
            </div>

            {/* Right Controls Panel (xl:col-span-5 2xl:col-span-4) */}
            <div className="xl:col-span-5 2xl:col-span-4 space-y-4">
              {/* Color Keying Selection */}
              <div className="bg-slate-50 dark:bg-slate-950/60 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  1. Target Background Color
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: 'White', color: '#ffffff' },
                    { label: 'Black', color: '#000000' },
                    { label: 'Green', color: '#00ff00' },
                    { label: 'Blue', color: '#0000ff' },
                  ].map((preset) => (
                    <button
                      key={preset.color}
                      type="button"
                      onClick={() => {
                        setSelectedColor(preset.color);
                        if (imageFile) processRemoval(imageFile, preset.color, tolerance, feather, bgType, replacementColor);
                      }}
                      className={`p-2.5 rounded-xl text-xs font-bold border flex items-center justify-center gap-1.5 transition-all ${
                        selectedColor.toLowerCase() === preset.color
                          ? 'border-pink-600 bg-pink-50 dark:bg-pink-950 text-pink-700 dark:text-pink-300 ring-2 ring-pink-500/30'
                          : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:border-pink-300'
                      }`}
                    >
                      <span
                        className="w-3.5 h-3.5 rounded-full border border-slate-300 shadow-sm"
                        style={{ backgroundColor: preset.color }}
                      />
                      <span>{preset.label}</span>
                    </button>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Custom Hex Color:</span>
                  <input
                    type="color"
                    value={selectedColor}
                    onChange={(e) => {
                      setSelectedColor(e.target.value);
                      if (imageFile) processRemoval(imageFile, e.target.value, tolerance, feather, bgType, replacementColor);
                    }}
                    className="w-9 h-9 rounded-xl border border-slate-200 dark:border-slate-800 cursor-pointer"
                  />
                </div>
              </div>

              {/* Tolerance & Feather Sliders */}
              <div className="bg-slate-50 dark:bg-slate-950/60 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  2. Edge & Sensitivity Tuning
                </h4>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                    <span>Color Tolerance</span>
                    <span className="font-mono text-pink-600 dark:text-pink-400">{tolerance}%</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="80"
                    value={tolerance}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      setTolerance(val);
                      if (imageFile) processRemoval(imageFile, selectedColor, val, feather, bgType, replacementColor);
                    }}
                    className="w-full accent-pink-600 cursor-pointer"
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                    <span>Edge Feathering</span>
                    <span className="font-mono text-pink-600 dark:text-pink-400">{feather}px</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={feather}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      setFeather(val);
                      if (imageFile) processRemoval(imageFile, selectedColor, tolerance, val, bgType, replacementColor);
                    }}
                    className="w-full accent-pink-600 cursor-pointer"
                  />
                </div>
              </div>

              {/* Backdrop Replacer */}
              <div className="bg-slate-50 dark:bg-slate-950/60 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  3. New Background Backdrop
                </h4>

                <div className="grid grid-cols-3 gap-2">
                  {(['transparent', 'color', 'gradient'] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        setBgType(type);
                        if (imageFile) processRemoval(imageFile, selectedColor, tolerance, feather, type, replacementColor);
                      }}
                      className={`py-2 px-2 rounded-xl text-xs font-bold capitalize border transition-all ${
                        bgType === type
                          ? 'bg-pink-600 text-white border-pink-600 shadow-md'
                          : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-pink-300'
                      }`}
                    >
                      {type === 'transparent' ? '🏁 Transparent' : type === 'color' ? '🎨 Color' : '✨ Gradient'}
                    </button>
                  ))}
                </div>

                {bgType === 'color' && (
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Backdrop Color:</span>
                    <input
                      type="color"
                      value={replacementColor}
                      onChange={(e) => {
                        setReplacementColor(e.target.value);
                        if (imageFile) processRemoval(imageFile, selectedColor, tolerance, feather, bgType, e.target.value);
                      }}
                      className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-800 cursor-pointer"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showBrushModal && imageFile && processedUrl && (
        <BackgroundBrushModal
          originalImage={imageFile}
          currentProcessedUrl={processedUrl}
          onApply={(dataUrl) => setProcessedUrl(dataUrl)}
          onClose={() => setShowBrushModal(false)}
        />
      )}
    </div>
  );
}
